<template>
  <div>
    <v-layout row wrap>
      <v-flex xs12 md4>
        <v-subheader>Name</v-subheader>
      </v-flex>
      <v-flex xs12 md8>
        <v-text-field v-if="editable" v-model="model.name"/>
        <div v-else class="data-display">{{ value.name }}</div>
      </v-flex>
    </v-layout>
    <v-layout row wrap class="buffer">
      <v-flex xs12 md4>
        <v-subheader v-if="type === 'group'">Manager</v-subheader>
        <v-subheader v-else>Approvers</v-subheader>
      </v-flex>
      <v-flex xs12 md8>
        <v-layout row wrap>
          <v-flex xs12 v-for="(approver, i) in model.approvers" :key="i">
            <single-select-search v-if="editable" :value="model.approvers[i]"
                                  @input="updateManager($event, i)"
                                  :options="managerFilter(model.approvers[i])" labelpath="name"
                                  keypath="aoid" minchars="2"/>
            <div v-else class="data-display">
              <template v-if="model.approvers[i]">{{ userName(model.approvers[i]) }}</template>
              <i v-else>None</i>
            </div>
          </v-flex>
          <v-flex xs12 v-if="editable && type !== 'group'">
            <v-subheader>
              Add approver
              <v-btn @click="addApprover" icon>
                <v-icon color="success">add_box</v-icon>
              </v-btn>
            </v-subheader>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <list-manager :value="model.servicePermissions" @input="setServices"
                  :options="serviceLabels" name="Service permission"
                  :editable="canManagePermissions" class="buffer"
                  :pagination.sync="permissionPagination"/>
    <template v-if="withUsers">
      <h3>Members</h3>
      <v-checkbox v-if="type === 'approval'" v-model="model.allUsers" label="All users"/>
      <template v-if="!model.allUsers">
        <list-manager :value="model.members" @input="setMembers" :options="userNames"
                      name="User" :editable="editable" select-type="search" class="buffer"
                      :pagination.sync="userPagination"/>
        <template v-if="type === 'approval'">
          <list-manager :value="model.groups" @input="setGroups" :options="groupNames"
                        :editable="editable" name="Group" select-type="search"
                        label=".name" class="buffer" :pagination.sync="groupPagination"/>
          <v-expansion-panel>
            <v-expansion-panel-content>
              <div slot="header">Included from groups</div>
              <list-manager :value="groupMembers" :editable="false" :options="[]"/>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </template>
      </template>
    </template>
    <div class="buttonContainer">
      <v-btn @click="goBack">Back</v-btn>
      <v-btn v-if="editable" color="success" @click="update">Update</v-btn>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import SingleSelectSearch from '@/views/shared/SingleSelectSearch';
  import ListManager from '@/views/shared/ListManager';

  export default {
    components: { SingleSelectSearch, ListManager },
    props: {
      value: {
        default: () => {
          return {
            id: null,
            name: null,
            approvers: [],
            servicePermissions: [],
            allUsers: false,
            members: [],
            groups: []
          };
        }
      },
      withUsers: { default: true },
      type: {}
    },
    data() {
      let approvers = this.value.approvers;
      if (approvers.length === 0) {
        approvers = [null];
      }

      return {
        model: {
          id: this.value.id,
          name: this.value.name,
          approvers,
          servicePermissions: this.value.servicePermissions.slice(),
          allUsers: this.value.allUsers,
          members: this.value.members.slice(),
          groups: this.value.groups.slice()
        },
        permissionPagination: { rowsPerPage: -1 },
        userPagination: { rowsPerPage: -1 },
        groupPagination: { rowsPerPage: -1 }
      };
    },
    computed: {
      editable() {
        return this.$auth.hasPermission('EDIT_ALL_USERS');
      },
      canManagePermissions() {
        return this.editable &&
          this.user.scopes.auth.servicePermissions.indexOf('GRANT_AUTH_PERMISSIONS') > -1;
      },
      serviceLabels() {
        return this.allServicePermissions.map((s) => {
          return {
            label: s.label,
            value: s.code
          };
        });
      },
      userNames() {
        const names = this.users.map((u) => ({
          label: u.name,
          value: u.aoid
        }));
        if (names.filter((n) => n.value === this.user.sub).length === 0) {
          names.push({ label: this.user.name, value: this.user.sub });
        }

        return names;
      },
      groupNames() {
        return this.groups.map((g) => {
          return {
            label: g.name,
            value: g.id
          };
        });
      },
      groupMembers() {
        return this.users.filter((u) => u.group && this.model.groups.indexOf(u.group) > -1)
          .map((u) => u.name);
      },
      ...mapState('auth', [
        'user'
      ]),
      ...mapState('users', [
        'users'
      ]),
      ...mapState('groups', [
        'groups'
      ]),
      ...mapState('permissions', {
        allServicePermissions: 'servicePermissions'
      })
    },
    methods: {
      userName(id) {
        const user = this.userNames.filter((n) => n.value === id);

        return user.length ? user[0].label : id;
      },
      updateManager(val, i) {
        if (val === null && this.model.approvers.length > 1) {
          this.model.approvers.splice(i, 1);
        } else {
          this.model.approvers.splice(i, 1, val);
        }
      },
      addApprover() {
        if (this.model.approvers.filter((u) => u === null).length === 0) {
          this.model.approvers.push(null);
        }
      },
      managerFilter(selectedManager) {
        return this.users.filter((u) =>
          u.aoid === selectedManager || this.model.approvers.indexOf(u.aoid) === -1
        );
      },
      setServices(val) {
        this.model.servicePermissions = val;
      },
      setMembers(val) {
        this.model.members = val;
      },
      setGroups(val) {
        this.model.groups = val;
      },
      goBack() {
        if (window.history.length > 1) {
          this.$router.go(-1);
        } else {
          this.$router.push('/groups');
        }
      },
      update() {
        this.$emit('input', {
          id: this.model.id,
          name: this.model.name,
          approvers: this.model.approvers.filter((u) => u !== null),
          servicePermissions: this.model.servicePermissions.slice(),
          allUsers: this.model.allUsers,
          members: this.model.members.slice(),
          groups: this.model.groups.slice()
        });
      },
      ...mapActions({
        initializeUsers: 'users/initialize',
        initializeGroups: 'groups/initialize',
        initializePermissions: 'permissions/initialize'
      })
    },
    mounted() {
      Promise.all([
        this.initializeUsers(),
        this.initializeGroups(),
        this.initializePermissions()
      ]).catch(this.$defaultHandlerHttp);
    }
  };
</script>

<style lang="less" scoped>
  @import '../../../style/form.less';

  h3 {
    margin-bottom: 20px;
  }
</style>
